import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBalanceScale,
    FaDatabase,
    FaFileAlt,
    FaGavel,
    FaLayerGroup,
    FaLock,
    FaShieldAlt,
    FaUsers,
    FaVoteYea
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChartPie,
    TiChevronRight,
    TiFlash,
    TiTick
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';
import ZC1DemoSection from './ZC1DemoSection';

gsap.registerPlugin(ScrollTrigger);

const Governance = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const stats = [
    { value: "42", label: "Active Proposals", icon: <FaFileAlt className="text-cyan-400" /> },
    { value: "89%", label: "Avg. Participation", icon: <FaUsers className="text-purple-400" /> },
    { value: "650M+", label: "TOTAL ZERO Staked", icon: <TiChartPie className="text-emerald-400" /> },
    { value: "$25M+", label: "DAO Treasury", icon: <FaDatabase className="text-orange-400" /> },
  ];

  const governancePillars = [
    {
      title: "On-Chain Governance",
      description: "Every proposal, vote, and execution is recorded immutably on the Zero-Blockchain. Transparency is not just a feature; it's our foundation.",
      icon: <FaShieldAlt />,
      color: "cyan",
      tag: "Immutable"
    },
    {
      title: "Staking-Weighted Power",
      description: "Your voice in the DAO is proportional to your long-term commitment. More tokens staked for longer periods grant higher voting weight.",
      icon: <FaBalanceScale />,
      color: "purple",
      tag: "Proportional"
    },
    {
      title: "Treasury Oversight",
      description: "Strategic fund management through community-elected committees, ensuring capital is always working for the ecosystem's future.",
      icon: <FaGavel />,
      color: "emerald",
      tag: "Elected"
    },
    {
      title: "DAO Conservation",
      description: "Directing treasury funds to global ecological projects. Turn your governance power into real-world environmental impact.",
      icon: <TiFlash />,
      color: "orange",
      tag: "Impactful"
    }
  ];

  const proposalLifecycle = [
    { step: "Submission", desc: "Open a formal proposal on the Zero-Governance portal with technical specs.", icon: <FaFileAlt /> },
    { step: "Community Debate", desc: "Participate in 7 days of rigorous discourse on the DAO forums.", icon: <FaUsers /> },
    { step: "Snapshot Voting", desc: "Token holders cast zero-gas votes on the proposed changes.", icon: <FaVoteYea /> },
    { step: "On-Chain Execution", desc: "Successful proposals are automatically implemented via autonomous contracts.", icon: <FaLock /> }
  ];

  const consensusDetails = [
    {
      title: "Proof-of-Stake (PoS)",
      features: [
        "Energy-Efficient Security",
        "Reward Distribution to Stakers",
        "Economic Sybil Protection",
        "Lower Barrier to Validation"
      ],
      icon: <TiChartPie className="text-cyan-400" />
    },
    {
      title: "Byzantine Fault Tolerance (BFT)",
      features: [
        "Instant Transaction Finality",
        "High Network Resilience",
        "Malicious Actor Resistance",
        "Low Latency Consensus"
      ],
      icon: <FaShieldAlt className="text-emerald-400" />
    },
    {
      title: "Directed Acyclic Graph (DAG)",
      features: [
        "Parallel Transaction Processing",
        "Near-Infinite Scalability",
        "Feeless Microtransactions",
        "Asynchronous Consensus"
      ],
      icon: <FaLayerGroup className="text-purple-400" />
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/governance-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="governanceHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Architecture of Consensus"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Powered by ZC-1 Hybrid Protocol (PoS + BFT + DAG)
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* DAO Network Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-48" ref={(el) => (sectionRefs.current[0] = el)}>
           {stats.map((stat, index) => (
             <motion.div
               key={index}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: index * 0.1 }}
               className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:border-cyan-500/40 transition-all group relative overflow-hidden"
             >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-3xl mb-4 group-hover:scale-110 transition-transform origin-left">{stat.icon}</div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{stat.label}</div>
             </motion.div>
           ))}
        </div>

        {/* Governance Introduction */}
        <div className="grid lg:grid-cols-2 gap-20 mb-48 items-center" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="relative">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px]" />
            <div className="flex items-center gap-3 text-cyan-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-8">
              <span className="w-12 h-px bg-cyan-400"></span>
              Decentralised Authority
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-[1.15]">
              Governed by the <span className="text-cyan-400 italic font-zentry">Many</span>, Controlled by <span className="text-cyan-400 italic font-zentry">None</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              The Zero-Blockchain is a self-evolving protocol. 
              Our governance model ensures that every major technical shift, treasury expenditure, 
              and ecosystem partnership is vied for, debated, and ultimately decided by the community.
            </p>
            <div className="flex flex-wrap gap-4">
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">Sybil Resistant</span>
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">DAO Enabled</span>
               <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300">Immutable Voting</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
             {governancePillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="p-8 bg-white/5 border border-white/10 rounded-[2rem] hover:bg-white/[0.08] transition-all cursor-default"
                >
                  <div className={`w-12 h-12 rounded-2xl bg-${pillar.color}-500/10 flex items-center justify-center text-2xl text-${pillar.color}-400 mb-6 border border-white/5`}>
                    {pillar.icon}
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-white">{pillar.title}</h4>
                    <span className="text-[8px] uppercase tracking-tighter px-2 py-0.5 rounded-full bg-white/10 text-gray-500 border border-white/5">{pillar.tag}</span>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">{pillar.description}</p>
                </motion.div>
             ))}
          </div>
        </div>

        {/* Proposal Lifecycle */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="text-center mb-24">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase tracking-wider">Proposal Lifecycle</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">The transparent path from a radical idea to an on-chain protocol upgrade.</p>
          </div>

          <div className="relative">
             {/* Background Connector Line */}
             <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent hidden lg:block" />
             
             <div className="grid lg:grid-cols-4 gap-8">
                {proposalLifecycle.map((item, index) => (
                   <motion.div
                     key={index}
                     initial={{ opacity: 0, y: 20 }}
                     whileInView={{ opacity: 1, y: 0 }}
                     viewport={{ once: true }}
                     transition={{ delay: index * 0.1 }}
                     className="relative bg-black border border-white/10 rounded-[3rem] p-10 flex flex-col items-center text-center group hover:border-cyan-500/50 transition-all"
                   >
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-black font-bold text-xs shadow-[0_0_20px_rgba(34,211,238,0.5)]">
                        {index + 1}
                      </div>
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-3xl text-cyan-400 mb-8 border border-white/5 group-hover:scale-110 group-hover:bg-cyan-500/10 transition-all">
                        {item.icon}
                      </div>
                      <h4 className="text-xl font-bold text-white mb-4">{item.step}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                      
                      {index < 3 && (
                        <div className="absolute -right-4 top-1/2 -translate-y-1/2 text-2xl text-cyan-500/30 hidden lg:block">
                          <TiChevronRight />
                        </div>
                      )}
                   </motion.div>
                ))}
             </div>
          </div>
        </div>

        {/* ZC-1 Technical Advantage */}
        <div className="mb-48 p-12 md:p-24 bg-white/5 border border-white/10 rounded-[4rem] relative overflow-hidden" ref={(el) => (sectionRefs.current[3] = el)}>
           <div className="absolute top-0 right-0 p-10 opacity-5 text-[15rem] pointer-events-none rotate-12">
              <FaLayerGroup />
           </div>
           
           <div className="relative z-10">
              <div className="text-center mb-20">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-widest leading-tight">
                  ZC-1 <span className="text-cyan-400 italic font-zentry">Hybrid Protocol</span>
                </h3>
                <p className="text-gray-300 max-w-3xl mx-auto text-lg">
                  Consensus redefined. Combining the economic finality of BFT, the decentralised scaling of PoS, and the parallel throughput of DAG architecture.
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                 {consensusDetails.map((detail, index) => (
                    <div key={index} className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-[3rem] p-12 hover:bg-white/[0.05] transition-all group">
                       <div className="flex items-center gap-6 mb-10">
                          <div className="w-16 h-16 rounded-3xl bg-white/5 flex items-center justify-center text-4xl border border-white/5 group-hover:border-white/20 transition-all">
                            {detail.icon}
                          </div>
                          <h4 className="text-2xl font-bold text-white">{detail.title}</h4>
                       </div>
                       <ul className="space-y-6">
                          {detail.features.map((feature, fIdx) => (
                             <li key={fIdx} className="flex items-center gap-4 text-gray-300 text-lg">
                                <span className="text-cyan-500 text-xl"><TiTick /></span>
                                {feature}
                             </li>
                          ))}
                       </ul>
                    </div>
                 ))}
              </div>
           </div>
        </div>

        {/* ZC-1 Interactive Demo */}
        <ZC1DemoSection />

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-500/30 to-transparent" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-3xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-5xl text-cyan-400 mx-auto mb-12 shadow-[0_0_50px_rgba(34,211,238,0.2)]">
              <FaBalanceScale />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Lead the <span className="font-zentry italic text-cyan-400 text-6xl">Evolution</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed">
              Zero-Blockchain isn't just a ledger; it's a community-owned institution. 
              Stake your claim, propose the next breakthrough, and help us scale the future of decentralised finance and global conservation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                onClick={() => navigate('/ico')}
                variant="cyan"
                size="lg"
                className="px-8 py-6"
              >
                Join the DAO <TiArrowRight className="text-2xl" />
              </GlassButton>
              
              <GlassButton
                onClick={() => window.open('/Whitepapers/Zero-Blockchain_Consensus_Report_ZC-1_Final.docx')}
                variant="gray"
                size="lg"
                className="px-8 py-6"
              >
                Consensus Report <TiFlash className="text-2xl text-cyan-400" />
              </GlassButton>
            </div>

            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-20 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaShieldAlt /> BFT FINALITY</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaUsers /> 89% ATTENDANCE</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaGavel /> DAO EXECUTED</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Governance;
